import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationCancel, NavigationEnd, NavigationError, Router, RouterStateSnapshot } from '@angular/router';
import { EMPTY, Observable, catchError, filter, map, of, switchMap, take, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { NavigationService } from '../../features/navigation/services/navigation.service';
import { SvcAppSettings } from 'projects/lib-shared-common/src/public-api';
import { Navigation } from '../../features/navigation/services/navigation.types';
import { StaticApplicationId } from '../../Constants/static-application-id.enum';
import { PageCantBeLoadedType } from '../../features/page-cant-be-load/page-cant-be-load.component';
import { UserService } from '../../features/user/services/user.service';

@Injectable({
  providedIn: 'root',
})
export class AvailableRouteGuard {
  /**
   * Constructor
   */
  constructor(
    private _router: Router,
    private _navigationService: NavigationService,
    private _userService: UserService,
    private _appSettings: SvcAppSettings
  ) {}
  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Can activate
   *
   * @param route
   * @param state
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    // AB#77806 - the menu check below runs in the background and may redirect. If that redirect fires
    // while the navigation that triggered this guard is still in progress, it cancels it and starts a new
    // one, which runs this guard again -> endless loop (module stuck on the splash screen). So, when a
    // navigation is in progress, only look at the menu once it has settled and only if the user is still
    // on that URL (if it was cancelled/superseded, the new navigation runs its own guards).
    this._waitForCurrentNavigationToSettle(state.url)
      .pipe(
        switchMap(() => this._navigationService.get(this._appSettings.applicationId as StaticApplicationId)),
        tap((response: Navigation) => {
          if (response.default) {
            for (const menu of response.default) {
              for (const subMenu of menu.children) {
                if (typeof subMenu.url === 'string' && state.url.startsWith(subMenu.url)) {
                  return;
                }
              }
            }
          }

          if (!Array.isArray(response.default) || !response.default.length) {
            throw new HttpErrorResponse({ status: 400 });
          }
          this._router.navigate(['/']);
        }),
        catchError<any, any>((error: HttpErrorResponse) => {
          this._navigationService.reset();
          if (error?.status === 400) {
            this._router.navigate(['page-cant-be-load'], {
              queryParams: {
                type: PageCantBeLoadedType.PERMISSION,
                from: this._router.url,
              },
            });
          } else {
            this._router.navigate(['page-cant-be-load']);
          }
          return EMPTY;
        }) as any
      )
      .subscribe();
    return of(true);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Emits once (and completes) right away when no navigation is in progress (unchanged behaviour).
   * Otherwise waits for the navigation to end and emits only if the router is still on `url`;
   * completes without emitting when that navigation was cancelled/superseded.
   */
  private _waitForCurrentNavigationToSettle(url: string): Observable<void> {
    if (!this._router.getCurrentNavigation()) {
      return of(void 0);
    }
    const path = (value: string) => (value ?? '').split('?')[0].split('#')[0];
    return this._router.events.pipe(
      filter((event) => event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError),
      take(1),
      filter(() => path(this._router.url) === path(url)),
      map(() => void 0)
    );
  }
}
